'use client'

import { useCallback } from 'react'

import { useForm, useWatch } from 'react-hook-form'

import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { TransactionMemoInput } from '~/components/inputs/custom/TransactionMemoInput'
import { useActiveOrg } from '~/hooks/org'
import { useUpdateAssetTransferMemo } from '~/hooks/useAssetTransfers'
import { useUpdateTransactionMemo } from '~/hooks/useTransactions'
import { OrgAssetTransfer, OrgTransaction } from '~/types'

export const UpdateTransactionMemoDialog = ({
  transaction,
  setIsOpen,
  onSuccess,
}: {
  transaction: OrgAssetTransfer | OrgTransaction
  setIsOpen: (isOpen: boolean) => void
  onSuccess?: () => void
}) => {
  const {
    updateMemo: updateAssetTransferMemo,
    isProcessing: isProcessingAssetTransfer,
  } = useUpdateAssetTransferMemo()
  const {
    updateMemo: updateTransactionMemo,
    isProcessing: isProcessingTransaction,
  } = useUpdateTransactionMemo()

  const isProcessing = transaction.isSplitsTransaction
    ? isProcessingTransaction
    : isProcessingAssetTransfer

  const { control, handleSubmit } = useForm<{ memo: string }>({
    defaultValues: {
      memo: transaction.memo ?? '',
    },
  })

  const { org } = useActiveOrg()
  const requireMemo = org?.requireMemo ?? false

  const onUpdate = handleSubmit(
    useCallback(
      async ({ memo }: { memo: string }) => {
        if (requireMemo && !memo?.trim()) return

        if (transaction.isSplitsTransaction) {
          await updateTransactionMemo(transaction, memo)
        } else {
          await updateAssetTransferMemo(transaction, memo)
        }
        onSuccess?.()
        setIsOpen(false)
      },
      [
        transaction,
        updateAssetTransferMemo,
        updateTransactionMemo,
        setIsOpen,
        onSuccess,
        requireMemo,
      ],
    ),
  )

  const memo = useWatch({ control, name: 'memo' })
  const hasMemoChanged = memo !== transaction.memo
  const isEditing = transaction.memo
  const isEmpty = !memo?.trim()
  const isDisabled =
    isProcessing ||
    (isEditing ? !hasMemoChanged : isEmpty) ||
    (requireMemo && isEmpty)
  const isRemoving = isEditing && isEmpty && !requireMemo

  return (
    <SplitsDialogContent
      id="updateTransactionMemo"
      title={`${isEditing ? 'Edit' : 'Add'} transaction memo`}
      variant="cta"
      isLoading={isProcessing}
      actionButton={{
        onClick: onUpdate,
        text: isRemoving ? 'Remove' : 'Update',
        eventName: 'clickedUpdateTransactionMemo',
        isDisabled,
        eventProperties: {
          transactionId: transaction.id,
        },
      }}
      cancelButton={{
        eventName: 'clickedCancelUpdateTransactionMemo',
        eventProperties: {
          transactionId: transaction.id,
        },
      }}
    >
      <form onSubmit={onUpdate}>
        <TransactionMemoInput control={control} inputName={'memo'} autoFocus />
      </form>
    </SplitsDialogContent>
  )
}
